<template>
  <div v-if="_service" class="w-full px-6 py-4">
    <div class="flex space-x-4 mb-6">
      <OFieldGroup col reverse class="w-full" :label="$t('components.service.edit.online.title')" :info="$t('components.service.edit.online.info')">
        <ToggleField v-model="_service.allowInternet" />
      </OFieldGroup>
    </div>

    <div class="bg-gray-50 border rounded-md p-3 mb-6 dark:bg-gray-900">
      <div class="flex items-center justify-between class border-b pb-2 mb-6">
        <div class="mr-10">
          <h5 class="text-gray-700 font-medium dark:text-gray-200">
            {{ $t('components.service.edit.online.deposit') }}
          </h5>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.service.edit.online.depositInfo') }}
          </p>
        </div>

        <div class="flex items-center space-x-2">
          <SegmentField v-model="overrideDepositFixed" :options="options" class="bg-gray-100 dark:bg-gray-900" />
        </div>
      </div>

      <div>
        <p v-if="!overrideDepositFixed" class="text-sm text-gray-700 -mt-2 dark:text-gray-200">
          {{ $t('components.service.edit.online.usingDefault') }}
        </p>

        <OFieldGroup v-else class="w-full" :label="$t('general.amount')">
          <div class="flex items-center space-x-2">
            <CurrencyField v-if="isOnlinePaymentDepositFixedTypeFixed" v-model="_service.onlinePaymentDepositFixed" class="w-1/2" />
            <NumberField
              v-if="isOnlinePaymentDepositFixedTypePercentage"
              v-model="_service.onlinePaymentDepositFixed"
              slim
              trailing="%"
              class="w-1/2"
            />
            <SegmentField v-model="_service.onlinePaymentDepositFixedType" :options="onlinePaymentDepositFixedTypeOptions" class="border w-1/2" />
          </div>
        </OFieldGroup>
      </div>
    </div>

    <div class="bg-gray-50 border rounded-md p-3 mb-6 dark:bg-gray-900">
      <div class="flex items-center justify-between class border-b pb-2 mb-6">
        <div class="mr-10">
          <h5 class="text-gray-700 font-medium dark:text-gray-200">
            {{ $t('components.service.edit.online.cardCapture') }}
          </h5>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.service.edit.online.cardCaptureInfo') }}
          </p>
        </div>

        <div class="flex items-center space-x-2">
          <SegmentField v-model="overrideCardCapture" :options="options" class="bg-gray-100 dark:bg-gray-900" />
        </div>
      </div>

      <div>
        <p v-if="!overrideCardCapture" class="text-sm text-gray-700 -mt-2 dark:text-gray-200">
          {{ $t('components.service.edit.online.usingDefault') }}
        </p>

        <OFieldGroup
          v-else
          col
          reverse
          class="w-full"
          :label="$t('components.service.edit.online.cardCaptureRequired')"
          :info="$t('components.service.edit.online.cardCaptureInfo')"
        >
          <ToggleField v-model="_service.onlinePaymentRequireCardCapture" />
        </OFieldGroup>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { OFieldGroup } from '@/components/elements'
import {
  CurrencyField,
  ToggleField,
  SegmentField,
  NumberField,
} from '@/components/fields'

import type { Service } from '~/_api/models/service'
import { ServiceOnlinePaymentDepositFixedType, ServiceOnlinePaymentDepositFixedTypeLabels } from '~/_api/models/service'

// Use Nuxt app composables
const { $api, $i18n } = useNuxtApp() as any

// Reactive data
const overrideCardCapture = ref<boolean>(false)
const overrideDepositFixed = ref<boolean>(false)

// Model bindings
const _service = defineModel<Service>('service')

const options = computed(() => [
  { label: $i18n.t('general.default'), value: false },
  { label: $i18n.t('general.override'), value: true },
])

const onlinePaymentDepositFixedTypeOptions = computed(() => {
  // Numeric TS enums return both keys (strings) and values (numbers) from
  // Object.values — filter to the numeric side only, then look up the
  // i18n key via the companion Labels map. Previously this read `.description`
  // off the old Enum wrapper; in the TS enum era the value is a plain number.
  return Object.values(ServiceOnlinePaymentDepositFixedType)
    .filter((v): v is ServiceOnlinePaymentDepositFixedType => typeof v === 'number')
    .map((value) => ({
      label: $i18n.t(ServiceOnlinePaymentDepositFixedTypeLabels[value]),
      value,
    }))
})

const isOnlinePaymentDepositFixedTypeFixed = computed(() => {
  return _service.value.onlinePaymentDepositFixedType === ServiceOnlinePaymentDepositFixedType.fixed
})

const isOnlinePaymentDepositFixedTypePercentage = computed(() => {
  return _service.value.onlinePaymentDepositFixedType === ServiceOnlinePaymentDepositFixedType.percentage
})

// Watchers
watch(overrideCardCapture, (value) => {
  if (value) {
    _service.value.onlinePaymentRequireCardCapture = _service.value.onlinePaymentRequireCardCapture !== null ? _service.value.onlinePaymentRequireCardCapture : true
  } else {
    _service.value.onlinePaymentRequireCardCapture = null
  }
})

watch(overrideDepositFixed, (value) => {
  if (value) {
    _service.value.onlinePaymentDepositFixed = _service.value.onlinePaymentDepositFixed !== null ? _service.value.onlinePaymentDepositFixed : 0
  } else {
    _service.value.onlinePaymentDepositFixed = null
  }
})

// Lifecycle
onMounted(() => {
  overrideDepositFixed.value = _service.value.onlinePaymentDepositFixed !== null
  overrideCardCapture.value = _service.value.onlinePaymentRequireCardCapture !== null
})
</script>
